import React from 'react';
import styled from 'styled-components';
import { SkeletonLoader } from './BasicSkeletonShapes';
import { colorBackground, colorBorder } from '../../../utils/palette';

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(${(props) => props.columnsCount || 6}, 1fr);
  gap: 16px;
  padding: 12px 24px;
  border-bottom: 1px solid ${colorBorder};
  background: ${(props) => props.headerBgColor || colorBackground};
`;

const TableRow = styled(TableHeader)`
  border-bottom: 1px solid ${colorBorder};
  background: ${(props) => props.rowBgColor || colorBackground};
  padding: 12px 24px;
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
`;

const PaginationButtonsWrapper = styled.div`
  display: flex;
  gap: 8px;
`;

export const TableSkeleton = ({
  isLoading = true,
  rowsCount = 4,
  columnsCount = 6,
  showPagination = true,
  headerCellWidth = '120px',
  rowCellWidth = '100px',
  bgColor = 'rgba(50, 88, 183, 0.1)',
  waveColor = 'rgba(38, 37, 37, 0.3)',
  headerHeight = '24px',
  rowHeight = '20px',
  paginationHeight = '24px',
  paginationInfoWidth = '120px',
  paginationButtonWidth = '32px',
  paginationButtonsCount = 4,
  headerBgColor,
  rowBgColor,
  children,
}) => {
  if (!isLoading) {
    return children;
  }

  const renderHeaderCells = () => {
    return Array.from({ length: columnsCount }).map((_, index) => (
      <SkeletonLoader
        key={`header-cell-${index}`}
        isLoading={true}
        height={headerHeight}
        width={headerCellWidth}
        bgColor={bgColor}
        waveColor={waveColor}
      />
    ));
  };

  const renderRows = () => {
    return Array.from({ length: rowsCount }).map((_, rowIndex) => (
      <TableRow
        key={`row-${rowIndex}`}
        columnsCount={columnsCount}
        rowBgColor={rowBgColor}
      >
        {Array.from({ length: columnsCount }).map((_, cellIndex) => (
          <SkeletonLoader
            key={`row-${rowIndex}-cell-${cellIndex}`}
            isLoading={true}
            height={rowHeight}
            width={rowCellWidth}
            bgColor={bgColor}
            waveColor={waveColor}
          />
        ))}
      </TableRow>
    ));
  };

  const renderPaginationButtons = () => {
    return Array.from({ length: paginationButtonsCount }).map((_, index) => (
      <SkeletonLoader
        key={`pagination-button-${index}`}
        isLoading={true}
        height={paginationHeight}
        width={paginationButtonWidth}
        bgColor={bgColor}
        waveColor={waveColor}
      />
    ));
  };

  return (
    <TableWrapper>
      <TableHeader
        columnsCount={columnsCount}
        headerBgColor={headerBgColor}
      >
        {renderHeaderCells()}
      </TableHeader>
      {renderRows()}

      {showPagination && (
        <PaginationWrapper>
          <SkeletonLoader
            isLoading={true}
            height={paginationHeight}
            width={paginationInfoWidth}
            bgColor={bgColor}
            waveColor={waveColor}
          />
          <PaginationButtonsWrapper>{renderPaginationButtons()}</PaginationButtonsWrapper>
        </PaginationWrapper>
      )}
    </TableWrapper>
  );
};
